// src/components/atomic-crm/companies/CompanyCreate.tsx
import { useState } from "react";
import { useNavigate, Form, useCreateController, useGetIdentity } from "ra-core";
import { useFormContext } from "react-hook-form";
import { supabase } from "../providers/supabase/supabase";
import { Button } from "@/components/ds/ui/button";
import { Input } from "@/components/ds/ui/input";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { CompanyInputs } from "./CompanyInputs";
import { FormToolbar } from "@/components/ds/admin/simple-form";

export const CompanyCreate = () => {
  const { identity } = useGetIdentity();
  const createController = useCreateController({ redirect: "list" });
  const { save } = createController;

  const handleSave = (values: any) => {
    // Strip client-only fields
    const { _ai_confirmed, ...dataToSave } = values;
    return save(dataToSave);
  };

  return (
    <div className="max-w-3xl mx-auto mt-8 p-4">
      <Form defaultValues={{ sales_id: identity?.id }} onSubmit={handleSave}>
        <AutoFillForm />
      </Form>
    </div>
  );
};

const AutoFillForm = () => {
  const form = useFormContext();
  const navigate = useNavigate(); // 👈 Needed for the custom Cancel button
  const [url, setUrl] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [error, setError] = useState("");
  const aiConfirmed = form.watch("_ai_confirmed");
  const handleAutofill = async () => {
    if (!url.trim()) return;
    setAutofilling(true);
    setError("");
    setAutofilled(false);

    try {
      const normalizedUrl = url.startsWith("http") ? url : `https://${url}`;
      const { data, error: fnError } = await supabase.functions.invoke(
        "extract-company",
        { body: { url: normalizedUrl } },
      );

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      if (!data?.success) throw new Error("Auto-fill failed");

      const extractedData = {
        ...data.data,
        website: normalizedUrl,
      };

      // Set values and mark as dirty
      Object.entries(extractedData).forEach(([key, value]) => {
        form.setValue(key as any, value, {
          shouldDirty: true,
          shouldTouch: true,
        });
      });

      // Clear the confirmation checkbox since data just changed
      form.setValue("_ai_confirmed" as any, false);

      setAutofilled(true);
    } catch (err: any) {
      setError(err.message || "Failed to auto-fill. Please fill manually.");
    } finally {
      setAutofilling(false);
    }
  };

  const isSaveDisabled = autofilled && !aiConfirmed;

  return (
    <>
      {/* Quick-fill banner */}
      <div className="mb-6 p-4 border rounded-lg bg-gradient-to-br from-indigo-500/5 to-purple-500/5 border-indigo-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-indigo-500" />
          <h3 className="text-sm font-semibold">Quick Create from Website</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Paste a company URL and we'll auto-fill the details using AI.
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="https://www.example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAutofill()}
            disabled={autofilling}
          />
          <Button
            onClick={handleAutofill}
            disabled={autofilling || !url.trim()}
            type="button"
            className="gap-2"
          >
            {autofilling ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {autofilling ? "Extracting..." : "Auto-fill"}
          </Button>
        </div>
        {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
      </div>

      <CompanyInputs />

      {/* AI confirmation — only shows after autofill */}
      {autofilled && (
        <div className="mt-6 p-4 border rounded-md bg-amber-500/5 border-amber-500/30">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-500">
                AI-generated data — please verify
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                These fields were filled automatically from the provided URL.
                Please confirm they accurately describe the company before
                saving.
              </p>
            </div>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300"
              {...form.register("_ai_confirmed", {
                validate: (value) => {
                  if (autofilled && !value) {
                    return "Please confirm the AI-generated data is correct before saving.";
                  }
                  return true;
                },
              })}
            />
          </label>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-6">
        <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSaveDisabled}>
          Save
        </Button>
      </div>
    </>
  );
};